import { ReferenceDto } from './ReferenceDto';

export enum ReferenceType {
  JournalArticle = 1,
  ConferencePaper = 2,
  MagazineArticle = 3,
  Book = 4,
  BookSection = 5,
  WebPage = 6,
  BlogPost = 7,
  Report = 8,
}

export type ReferenceAuthor = {
  firstName?: string;
  lastName?: string;
};

type CommonReference = {
  id: string;
  referenceType: ReferenceType;
  title?: string;
  publishDate?: Date | null;
  abstract?: string;
  url?: string;
  language?: string;
  createdAt?: Date | null;
  creatorName?: string;
  updatedAt?: Date | null;
  editorId?: string | null;
  editorName?: string;
  doi?: string;
  isbn?: string;
  issn?: string;
  authors?: ReferenceAuthor[];
  tags?: string[];
  rawContent?: string;
  rawContentHash?: string;
};

export type JournalReference = CommonReference & {
  referenceType: ReferenceType.JournalArticle;
  extendedContent: {
    journalAbbreviation: string;
    volume: string;
    issue: string;
    pages: string;
  };
};

export type BookReference = CommonReference & {
  referenceType: ReferenceType.Book;
  extendedContent: {
    edition?: string;
    volume?: string;
    place?: string;
    publisher?: string;
  };
};

export type ConferencePaperReference = CommonReference & {
  referenceType: ReferenceType.ConferencePaper;
  extendedContent: {
    conferenceName?: string;
    place?: string;
    volume?: string;
    pages?: string;
  };
};

export type MagazineArticleReference = CommonReference & {
  referenceType: ReferenceType.MagazineArticle;
  extendedContent: {
    volume?: string;
    issue?: string;
    pages?: string;
    shortTitle?: string;
  };
};

export type BookSectionReference = CommonReference & {
  referenceType: ReferenceType.BookSection;
  extendedContent: {
    bookTitle?: string;
    edition?: string;
    volume?: string;
    place?: string;
    pages?: string;
    publisher?: string;
  };
};

export type WebPageReference = CommonReference & {
  referenceType: ReferenceType.WebPage;
  extendedContent: {
    websiteTitle?: string;
  };
};

export type BlogPostReference = CommonReference & {
  referenceType: ReferenceType.BlogPost;
  extendedContent: {
    blogTitle?: string;
  };
};

export type ReportReference = CommonReference & {
  referenceType: ReferenceType.Report;
  extendedContent: {
    reportNumber?: string;
    reportType?: string;
    place?: string;
    institution?: string;
    pages?: string;
  };
};

export type Reference =
  | JournalReference
  | BookReference
  | ConferencePaperReference
  | MagazineArticleReference
  | BookSectionReference
  | WebPageReference
  | BlogPostReference
  | ReportReference;

export const mapReferenceDtoToReference = (dto: ReferenceDto): Reference => {
  return {
    id: dto.id,
    title: dto.title,
    publishDate: dto.publishDate,
    abstract: dto.abstract,
    url: dto.url,
    referenceType: dto.referenceType?.valueOf() ?? ReferenceType.JournalArticle,
    language: dto.language,
    createdAt: dto.createdAt,
    creatorName: dto.creatorName,
    updatedAt: dto.updatedAt,
    editorId: dto.editorId,
    editorName: dto.editorName,
    doi: dto.doi,
    isbn: dto.isbn,
    issn: dto.issn,
    authors: dto.authors?.map((author) => ({
      firstName: author.firstName,
      lastName: author.lastName,
    })),
    tags: dto.tags,
    rawContent: dto.rawContent,
    rawContentHash: dto.rawContentHash,
    extendedContent: dto.extendedContent,
  };
};

export const mapReferenceToDto = (reference: Reference): ReferenceDto => {
  return {
    id: reference.id,
    title: reference.title,
    publishDate: reference.publishDate,
    abstract: reference.abstract,
    url: reference.url,
    referenceType: reference.referenceType?.valueOf() ?? ReferenceType.JournalArticle,
    language: reference.language,
    createdAt: reference.createdAt,
    creatorName: reference.creatorName,
    updatedAt: reference.updatedAt,
    editorId: reference.editorId,
    editorName: reference.editorName,
    doi: reference.doi,
    isbn: reference.isbn,
    issn: reference.issn,
    authors: reference.authors?.map((author) => ({
      firstName: author.firstName,
      lastName: author.lastName,
    })),
    tags: reference.tags,
    rawContent: reference.rawContent,
    rawContentHash: reference.rawContentHash,
    extendedContent: getExtendedContent(reference),
  };
};

const getExtendedContent = (reference: Reference): object => {
  switch (reference.referenceType) {
    case ReferenceType.JournalArticle:
      return {
        journalAbbreviation: reference.extendedContent.journalAbbreviation,
        volume: reference.extendedContent.volume,
        issue: reference.extendedContent.issue,
        pages: reference.extendedContent.pages,
      };
    case ReferenceType.Book:
      return {
        edition: reference.extendedContent.edition,
        volume: reference.extendedContent.volume,
        place: reference.extendedContent.place,
        publisher: reference.extendedContent.publisher,
      };
    case ReferenceType.BookSection:
      return {
        bookTitle: reference.extendedContent.bookTitle,
        edition: reference.extendedContent.edition,
        volume: reference.extendedContent.volume,
        place: reference.extendedContent.place,
        pages: reference.extendedContent.pages,
        publisher: reference.extendedContent.publisher,
      };
    case ReferenceType.ConferencePaper:
      return {
        conferenceName: reference.extendedContent.conferenceName,
        place: reference.extendedContent.place,
        volume: reference.extendedContent.volume,
        pages: reference.extendedContent.pages,
      };
    case ReferenceType.Report:
      return {
        reportNumber: reference.extendedContent.reportNumber,
        reportType: reference.extendedContent.reportType,
        place: reference.extendedContent.place,
        institution: reference.extendedContent.institution,
        pages: reference.extendedContent.pages,
      };
    case ReferenceType.BlogPost:
      return {
        blogTitle: reference.extendedContent.blogTitle,
      };
    case ReferenceType.WebPage:
      return {
        websiteTitle: reference.extendedContent.websiteTitle,
      };
    case ReferenceType.MagazineArticle:
      return {
        volume: reference.extendedContent.volume,
        issue: reference.extendedContent.issue,
        pages: reference.extendedContent.pages,
        shortTitle: reference.extendedContent.shortTitle,
      };
    default:
      return {};
  }
};

export type ReferenceEntry = {
  id: string;
  title: string;
  journalTitle: string;
  authors: Author[];
  publishDate: string;
  url: string;
  doi: string;
};

export type Author = {
  firstname: string;
  lastname: string;
  suffix: string;
};

export type ExtractReferenceResult = {
  newReferences?: Reference[];
  existingReferences?: Reference[];
};
